// src/components/GlobalFatalError.jsx
import React from "react";
import { Result, Flex, Typography } from "antd";
import { getCurrentLang } from "../../core/BXHeader/config/commonUtil";
const { Paragraph, Text } = Typography;

/**
 * GlobalFatalError
 * 用于展示致命错误（例如 Promise.withResolvers 在低版本浏览器缺失）
 * Props:
 * - error: Error
 * - errorInfo: { componentStack?: string }
 * - reset: () => void // 可选，点击“Refresh”时触发
 * - forceLocale: 'en' | 'ch' | undefined // 可选，强制文案语言（默认自动识别）
 *
 * 变更点（body 结构）：
 * - copy.body 现在支持 string 或 Block[]（向下兼容）
 * - Block 协议：{ type: 'p' | 'list' | 'link' | 'strong' | 'br', content?, items?, href? }
 */
export default function GlobalFatalError({ error, errorInfo, reset, forceLocale }) {
  const isDev = process.env.NODE_ENV !== "production";
  const msg = String(error?.message ?? "");

  // 语言识别：优先 forceLocale，其次自动
  const autoLang = getCurrentLang(); // 你的方法内部已根据 navigator.language 作判断
  const lang = forceLocale ?? autoLang;

  // 文案定义（示例：将 body 统一放入数组，更灵活）
  const dict = {
    en: {
      title: "Document Preview Not Supported",
      body: [
        { type: "p", content: "Your browser does not support viewing the uploaded document on this page." },
        { type: "p", content: "You can still proceed with submitting your application." },
        { type: "p", content: "To review the document, please download it to your device." },
        // 示例：如果要加帮助链接或列表
        // { type: "link", href: "https://example.com/help", content: "Open help / Supported browsers" },
        // { type: "list", items: ["Use the latest Chrome/Edge/Firefox", "Reload after clearing cache"] },
      ],
      primaryCta: "Open help / Supported browsers",
      secondaryCta: "Refresh",
      backHome: "Back to Home",
    },
    ch: {
      title: "不支援文件預覽",
      body: [
        { type: "p", content: "您的瀏覽器不支援在此頁面查看已上傳的文件。" },
        { type: "p", content: "您仍然可以繼續提交申請。" },
        { type: "p", content: "如需檢視文件，請下載到您的裝置。" },
        // { type: "link", href: "https://example.com/help", content: "開啟說明／支援的瀏覽器" },
        // { type: "list", items: ["建議使用最新版本 Chrome/Edge/Firefox", "清除快取後重新整理"] },
      ],
      primaryCta: "開啟說明／支援的瀏覽器",
      secondaryCta: "重新整理",
      backHome: "返回首頁",
    },
  };

  const copy = dict[lang] ?? dict.en;

  // 判断是否为 withResolvers 类错误
  const isResolversError = /withResolvers/i.test(msg) || /Promise\.withResolvers/i.test(msg);

  // 渲染器：支持 string（向下兼容）或 Block[]
  const renderBody = (body) => {
    if (!body) return null;

    // 旧结构兼容：string
    if (typeof body === "string") {
      return <Paragraph>{body}</Paragraph>;
    }

    // 新结构：Block[]
    if (Array.isArray(body)) {
      return body.map((block, idx) => {
        if (!block || typeof block !== "object") {
          return <Paragraph key={idx}>{String(block)}</Paragraph>;
        }
        const { type, content, items, href } = block;

        switch (type) {
          case "p":
            return <Paragraph key={idx}>{content}</Paragraph>;
          case "strong":
            return (
              <Paragraph key={idx}>
                <Text strong>{content}</Text>
              </Paragraph>
            );
          case "br":
            return <div key={idx} style={{ height: 8 }} />;
          case "list":
            return (
              <ul key={idx} style={{ paddingLeft: 20, margin: "4px 0 12px" }}>
                {(items ?? []).map((it, i2) => (
                  <li key={i2} style={{ lineHeight: 1.6 }}>
                    {it}
                  </li>
                ))}
              </ul>
            );
          case "link":
            return (
              <Paragraph key={idx}>
                <a href={href} target="_blank" rel="noreferrer">
                  {content ?? href}
                </a>
              </Paragraph>
            );
          default:
            return <Paragraph key={idx}>{content ?? ""}</Paragraph>;
        }
      });
    }

    // 兜底
    return <Paragraph>{String(body)}</Paragraph>;
  };

  // 可选：帮助链接地址（如有）
  // const HELP_URL = lang === 'ch' ? 'https://example.com/zh' : 'https://example.com/en';

  return (
    <Result status="error" title={copy?.title} subTitle={undefined} style={{ width: "100%", display: "block" }}>
      <div style={{ width: "100%" }}>{renderBody(copy?.body)}</div>
    </Result>
  );
}
